import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { formatDistanceToNowStrict } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale';
import { useAuth } from '../../../shared/hooks/useAuth';
import { useApiError } from '../../../shared/errors/useApiError';
import { useNotification } from '../../../shared/notifications/NotificationProvider';
import type { Comment } from '../../../shared/services/opportunities.service';
import { avatarColor, initials } from '../postVisuals';
import { useOpportunityMutations } from '../useOpportunities';

interface Props {
  postId: string;
  comments: Comment[];
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
}

/**
 * Renders a comment thread. Each row carries a three-dot menu when the
 * viewer can delete the comment ({@code canDelete} is provided by the
 * backend — true for the author or an admin).
 */
export function CommentList({
  postId,
  comments,
  isLoading,
  isError,
  hasMore,
  isFetchingMore,
  onLoadMore,
}: Props) {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const { deleteComment } = useOpportunityMutations();
  const dateLocale = i18n.language?.startsWith('fr') ? frLocale : undefined;

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuCommentId, setMenuCommentId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuCommentId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDeleteId) return;
    try {
      await deleteComment.mutateAsync({ commentId: confirmDeleteId, postId });
      notify.success(t('opportunities.comments.deleted'));
    } catch (err) {
      notify.error(translate(err));
    } finally {
      setConfirmDeleteId(null);
    }
  };

  if (isError) {
    // Anonymous viewers most often hit this branch when their JWT just
    // expired and the comments endpoint returned a 401 before the auth
    // context caught up. Frame it as a sign-in invitation rather than a
    // generic "server unavailable" message — the action they need to take
    // is clear from context.
    if (!isAuthenticated) {
      return (
        <Alert severity="info">
          {t('opportunities.comments.signInToReadOrComment')}
        </Alert>
      );
    }
    return <Alert severity="error">{t('opportunities.comments.loadError')}</Alert>;
  }

  if (isLoading) {
    return (
      <Stack spacing={2}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={70} />
        ))}
      </Stack>
    );
  }

  if (comments.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        {t('opportunities.comments.empty')}
      </Typography>
    );
  }

  return (
    <Stack spacing={2.5}>
      {comments.map((c) => (
        <Stack key={c.id} direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar
            src={c.author.avatarUrl ?? undefined}
            sx={{
              width: 36,
              height: 36,
              bgcolor: avatarColor(c.author.userId),
              color: 'common.white',
              fontSize: 13,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {initials(c.author.name)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }} noWrap>
                {c.author.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDistanceToNowStrict(new Date(c.createdAt), {
                  locale: dateLocale,
                  addSuffix: true,
                })}
              </Typography>
              {c.canDelete && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    setMenuAnchor(e.currentTarget);
                    setMenuCommentId(c.id);
                  }}
                  aria-label={t('common.actions')}
                  sx={{ ml: 'auto' }}
                >
                  <MoreVertRoundedIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
            <Typography
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: 14,
                lineHeight: 1.55,
                mt: 0.5,
              }}
            >
              {c.body}
            </Typography>
          </Box>
        </Stack>
      ))}

      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <Button
            onClick={onLoadMore}
            disabled={isFetchingMore}
            size="small"
            variant="outlined"
            sx={{ borderRadius: 999 }}
          >
            {isFetchingMore
              ? t('opportunities.loading')
              : t('opportunities.comments.loadMore')}
          </Button>
        </Box>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            if (menuCommentId) setConfirmDeleteId(menuCommentId);
            closeMenu();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('opportunities.actions.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={confirmDeleteId !== null}
        onClose={() => !deleteComment.isPending && setConfirmDeleteId(null)}
      >
        <DialogTitle>{t('opportunities.comments.deleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('opportunities.comments.deleteConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteId(null)}
            disabled={deleteComment.isPending}
          >
            {t('opportunities.form.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteComment.isPending}
            startIcon={
              deleteComment.isPending
                ? <CircularProgress size={14} color="inherit" />
                : null
            }
          >
            {t('opportunities.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
